import { MetricDef, MetricGroup, MetricsSeriesPoint } from './types'

/**
 * Sample weekly series for the headline chart. Replace with
 * data_ai_agent_runs + request completions grouped by week.
 */
export const SAMPLE_WEEKLY: MetricsSeriesPoint[] = [
  { t: 'W1', outcomes: 11, hours: 52, cost: 186 },
  { t: 'W2', outcomes: 13, hours: 49, cost: 174 },
  { t: 'W3', outcomes: 12, hours: 47, cost: 168 },
  { t: 'W4', outcomes: 15, hours: 44, cost: 159 },
  { t: 'W5', outcomes: 14, hours: 41, cost: 151 },
  { t: 'W6', outcomes: 16, hours: 39, cost: 147 },
  { t: 'W7', outcomes: 17, hours: 36, cost: 141 },
  { t: 'W8', outcomes: 18, hours: 34, cost: 138 },
]

export const SAMPLE_COST_BY_AGENT: Array<{ agent: string; usd: number }> = [
  { agent: 'Mo', usd: 42.1 },
  { agent: 'Detective', usd: 31.4 },
  { agent: 'Builder', usd: 58.7 },
  { agent: 'Planner', usd: 12.2 },
  { agent: 'QA', usd: 18.9 },
  { agent: 'Security', usd: 6.4 },
  { agent: 'UX', usd: 4.1 },
]

export const SAMPLE_STRATEGIC = {
  systemsPerEngineerBefore: 2.4,
  systemsPerEngineerAfter: 6.8,
  hoursPerOutcomeBefore: 6.5,
  hoursPerOutcomeAfter: 2.1,
  outcomesPerFteBefore: 9,
  outcomesPerFteAfter: 22,
}

const CLIENT_ROLES = 'CLIENT, CLIENT_GUEST, GUEST, STAKEHOLDER, PARTICIPANT'
const PM_ROLES = 'OWNER, TEAM_LEADER'

export const METRICS: MetricDef[] = [
  // ── 1. Client / stakeholder ──────────────────────────────────────────────
  {
    id: 'client_questions',
    name: 'Client questions',
    group: 'client',
    headline: true,
    side: 'external',
    tellsYou: 'How much the client is actually using Mo as the front door',
    formula: 'Count of client-authored question turns in the window',
    unit: 'count',
    sample: 148,
    grain: 'Mo user message that is a question (not a change request)',
    sources: ['data_mo_conversation_messages', 'data_mo_threads', 'data_user_roles', 'cat_roles'],
    calculate: `Join message.user_id → data_user_roles → cat_roles. Count role=user messages whose author role ∈ {${CLIENT_ROLES}}. Exclude turns that created a data_mo_tasks row / ticket in the same run_uuid (those are change requests).`,
    wire: 'proxy',
    gaps: 'No first-class question vs change-request column. Infer from whether Mo created a ticket/task in that run.',
  },
  {
    id: 'ai_question_resolution_rate',
    name: 'AI question resolution rate',
    group: 'client',
    headline: true,
    side: 'external',
    tellsYou: 'How many questions never require the team',
    formula: 'Questions resolved with no human handoff / client questions',
    unit: 'percent',
    sample: 0.71,
    grain: 'question',
    sources: ['data_mo_conversation_messages', 'data_mo_objective_memories', 'data_mo_tasks'],
    calculate: 'Denominator = client_questions. Numerator = those whose objective never set continuationIntent=escalate_human and no OWNER/TEAM_LEADER message on the thread before resolution (assistant kind=summary/text after last client turn, task phase=done or thread goes idle).',
    wire: 'proxy',
  },
  {
    id: 'human_escalation_rate',
    name: 'Human escalation rate',
    group: 'client',
    headline: true,
    side: 'external',
    tellsYou: 'Opposite of autonomy — how often the team must enter',
    formula: 'Questions handed to PM/team / questions',
    unit: 'percent',
    sample: 0.29,
    grain: 'question',
    sources: ['data_mo_objective_memories.memory_json', 'data_mo_conversation_messages'],
    calculate: 'continuationIntent=escalate_human OR Mo askKind=escalation_pushback OR data_mo_tasks.phase in (needs_attention, awaiting_input) after an AI error. Complementary to AI question resolution rate.',
    wire: 'existing',
  },
  {
    id: 'time_to_first_useful_response',
    name: 'Time to first useful response',
    group: 'client',
    headline: true,
    side: 'external',
    tellsYou: 'Whether clients get value immediately',
    formula: 'Median (first substantive assistant message − first client message) per thread',
    unit: 'duration_ms',
    sample: 84000,
    grain: 'Mo thread',
    sources: ['data_mo_conversation_messages'],
    calculate: 'Per thread_id: MIN(assistant.created_at where kind in text|summary|plan and content length > short ack) − MIN(user.created_at). Report median. Ignore kind=error.',
    wire: 'existing',
    gaps: 'Need a "substantive" rule: not "Gathering clues…". Prefer first assistant row with kind=text after Mo finished a specialist or answered without spinning.',
  },
  {
    id: 'time_to_resolution',
    name: 'Time to resolution',
    group: 'client',
    headline: true,
    side: 'external',
    tellsYou: 'Total client wait for an outcome',
    formula: 'Median (resolved_at − request/question created_at)',
    unit: 'duration_ms',
    sample: 22320000,
    grain: 'request / objective',
    sources: ['data_requests', 'data_mo_objective_memories', 'data_mo_tasks'],
    calculate: 'Questions: last assistant resolution message − first user message. Change requests: data_requests.delivery_on (or status=COMPLETED updated_at) − created_at. Prefer objective status=complete timestamp from memory revision.',
    wire: 'existing',
  },
  {
    id: 'ai_only_resolution_time',
    name: 'AI-only resolution time',
    group: 'client',
    tellsYou: 'Speed of autonomous service',
    formula: 'Median resolution time where no human (PM/dev) participated, excluding policy approvals',
    unit: 'duration_ms',
    sample: 5400000,
    grain: 'request / question',
    sources: ['data_mo_objective_memories', 'data_ticket_user_assignees', 'data_ai_agent_runs'],
    calculate: 'Same clock as time_to_resolution, filtered to outcomes with zero PM/dev assignee user_started_at and no escalate_human. Mandatory client approvals do not disqualify.',
    wire: 'proxy',
  },
  {
    id: 'human_assisted_resolution_time',
    name: 'Human-assisted resolution time',
    group: 'client',
    tellsYou: 'How well the fallback works',
    formula: 'Median resolution time for escalated cases',
    unit: 'duration_ms',
    sample: 61200000,
    grain: 'request / question',
    sources: ['data_mo_objective_memories', 'data_requests'],
    calculate: 'Subset of time_to_resolution where continuationIntent was escalate_human or a PM/dev touched the ticket.',
    wire: 'proxy',
  },
  {
    id: 'client_wait_time',
    name: 'Client wait time',
    group: 'client',
    tellsYou: 'Time Motionode/agency makes the client wait',
    formula: 'Sum of intervals waiting on agency/AI',
    unit: 'duration_ms',
    sample: 18720000,
    grain: 'request',
    sources: ['data_ticket_user_assignees'],
    calculate: 'Sum (unblocked_at − blocked_at) where blocker_category ≠ CLIENT, plus time in IN_PROGRESS / WAITING on agency-owned steps. Active wait: status=WAITING and waiting_for is not a client role.',
    wire: 'existing',
  },
  {
    id: 'agency_wait_time',
    name: 'Agency wait time',
    group: 'client',
    tellsYou: 'Separates delays the client caused',
    formula: 'Sum of intervals waiting on the client',
    unit: 'duration_ms',
    sample: 28800000,
    grain: 'request',
    sources: ['data_ticket_user_assignees', 'data_mo_objective_memories'],
    calculate: 'Sum blocked intervals where blocker_category=CLIENT, plus objective blockedOnUser=true windows, plus assignee waiting_for_user_uuid whose role is a client role.',
    wire: 'existing',
  },
  {
    id: 'client_approval_response_time',
    name: 'Client approval response time',
    group: 'client',
    tellsYou: 'How quickly clients respond when needed',
    formula: 'Median (approval/rejection − approval requested)',
    unit: 'duration_ms',
    sample: 39600000,
    grain: 'approval',
    sources: [
      'data_ai_chat_messages.approval_status',
      'data_project_activity_events',
      'data_requests.client_bill_status',
    ],
    calculate: 'Pair FEEDBACK_REQUESTED → CHANGE_APPROVED (or rejected). Also Builder messages pending→approved/denied, and request client_bill_status PROPOSED→APPROVED/REJECTED.',
    wire: 'existing',
  },
  {
    id: 'requests_created_through_chat',
    name: 'Requests created through chat',
    group: 'client',
    headline: true,
    side: 'external',
    tellsYou: 'Whether chat really replaces PM tooling',
    formula: 'Chat-originated requests / all requests created',
    unit: 'percent',
    sample: 0.64,
    grain: 'request',
    sources: ['data_requests', 'data_mo_tasks', 'data_mo_thread_entities'],
    calculate: 'Numerator: distinct request_uuid on data_mo_tasks OR thread_entities where entity_type=deliverable created in-window. Denominator: data_requests.created_at in window. There is no origin=chat column — this join is the origin.',
    wire: 'proxy',
    gaps: 'Optional later: stamp origin on data_requests when Mo creates it.',
  },
  {
    id: 'requests_created_without_manual_ticket_entry',
    name: 'Requests created without manual ticket entry',
    group: 'client',
    tellsYou: 'Value of automatic documentation',
    formula: 'Automatically structured work / total work',
    unit: 'percent',
    sample: 0.57,
    grain: 'ticket + request',
    sources: ['data_mo_tasks', 'data_tickets', 'data_tickets.scaffold_status'],
    calculate: 'Tickets with a matching data_mo_tasks.ticket_uuid, or scaffold_status in (DONE, RUNNING) from Mo, over tickets created in window. Subtract tickets whose first edit was a human rewriting title/steps within 10 minutes if you want "needed repair" out of this KPI (that belongs in manual_ticket_edits).',
    wire: 'proxy',
  },
  {
    id: 'repeat_mo_usage_7d',
    name: 'Repeat Mo usage (7 days)',
    group: 'client',
    tellsYou: 'Whether clients come back',
    formula: 'Clients with a Mo question on day 0 who ask again within 7 days / those clients',
    unit: 'percent',
    sample: 0.48,
    grain: 'client user',
    sources: ['data_mo_conversation_messages', 'data_mo_threads'],
    calculate: 'Cohort: distinct client user_ids with a question in the window start. Repeat: another user message on a later day ≤7d. Use thread.created_by_user_id or message.user_id.',
    wire: 'existing',
  },
  {
    id: 'repeat_mo_usage_30d',
    name: 'Repeat Mo usage (30 days)',
    group: 'client',
    headline: true,
    side: 'external',
    tellsYou: 'Whether Mo becomes habitual',
    formula: 'Same as 7-day, window 30 days',
    unit: 'percent',
    sample: 0.61,
    grain: 'client user',
    sources: ['data_mo_conversation_messages'],
    calculate: 'Same cohort logic with 30-day return.',
    wire: 'existing',
  },
  {
    id: 'questions_per_active_client',
    name: 'Questions per active client',
    group: 'client',
    tellsYou: 'Depth of use',
    formula: 'Client questions / active clients',
    unit: 'ratio',
    sample: 12.3,
    grain: 'client',
    sources: ['data_mo_conversation_messages', 'data_user_roles'],
    calculate: 'Active client = audience-role user with ≥1 Mo message or ticket touch in the window. Divide client_questions by that count.',
    wire: 'existing',
  },
  {
    id: 'requests_per_active_client',
    name: 'Requests per active client',
    group: 'client',
    tellsYou: 'Whether Mo is the normal front door',
    formula: 'Requests created / active clients',
    unit: 'ratio',
    sample: 3.4,
    grain: 'client',
    sources: ['data_requests', 'data_user_roles'],
    calculate: 'data_requests created in window / active clients (same definition as questions_per_active_client).',
    wire: 'existing',
  },
  {
    id: 'successful_fallback_rate',
    name: 'Successful fallback rate',
    group: 'client',
    tellsYou: 'Whether failure still produces a good service experience',
    formula: 'Escalations resolved without the client restarting or re-explaining / escalations',
    unit: 'percent',
    sample: 0.76,
    grain: 'escalation',
    sources: ['data_mo_threads', 'data_mo_conversation_messages', 'data_mo_objective_memories'],
    calculate: 'After escalate_human, the next client message is not a full restatement (heuristic: not a new thread, or embedding/similarity later). Until then: client did not open a new thread on the same ticket within 24h of the handoff.',
    wire: 'proxy',
    gaps: 'Re-explain detection is heuristic. Prefer: same thread_id continues after human_joined.',
  },
  {
    id: 'context_preserved_escalation_rate',
    name: 'Context-preserved escalation rate',
    group: 'client',
    headline: true,
    side: 'external',
    tellsYou: 'Handoffs arrive with investigation already done — Motionode-specific',
    formula: 'Escalations delivered with findings/evidence / escalations',
    unit: 'percent',
    sample: 0.82,
    grain: 'escalation',
    sources: ['data_agent_documents', 'data_agent_sessions', 'data_mo_objective_memories', 'data_ai_agent_runs.signals'],
    calculate: 'On escalate_human, require a specialist document (Detective/planner) on the ticket, or memory_json with investigation conclusions / evidence records, or data_ai_agent_runs.signals.root_cause_found for detective. This is the "82% arrived with investigation completed" headline.',
    wire: 'existing',
  },

  // ── 2. Coordination ──────────────────────────────────────────────────────
  {
    id: 'active_clients',
    name: 'Active clients',
    group: 'coordination',
    headline: true,
    side: 'internal',
    tellsYou: 'Maintained client count in the window',
    formula: 'Distinct audience-role users (or client orgs) with activity',
    unit: 'count',
    sample: 12,
    grain: 'client',
    sources: ['data_user_roles', 'data_projects', 'data_mo_conversation_messages'],
    calculate: 'Prefer distinct project+client-org with a request, ticket, or Mo message in-window. Do not count internal OWNER as a client.',
    wire: 'existing',
  },
  {
    id: 'requests_requiring_pm',
    name: 'Requests requiring PM involvement',
    group: 'coordination',
    tellsYou: 'PM dependency',
    formula: 'Requests where a PM takes an action / requests',
    unit: 'percent',
    sample: 0.38,
    grain: 'request',
    sources: ['data_ticket_user_assignees', 'data_assignation_logs', 'data_mo_conversation_messages'],
    calculate: `PM action = assignee or message author with role in {${PM_ROLES}}, excluding mandatory production approval if you tag those separately. Count distinct request_id.`,
    wire: 'proxy',
    gaps: 'Distinguish mandatory control (release approve) from PM coordination (routing, rewriting tickets, chasing people).',
  },
  {
    id: 'change_requests_build_without_pm',
    name: 'Change requests reaching Build without PM',
    group: 'coordination',
    tellsYou: 'The original Advante coordination metric',
    formula: 'Requests that reach a Build step with no PM action before that step / applicable change requests',
    unit: 'percent',
    sample: 0.44,
    grain: 'request',
    sources: ['data_tickets.current_production_step', 'data_production_steps', 'data_ticket_user_assignees'],
    calculate: 'Applicable = not discovery-only. Reach Build = ticket current step is the AI Builder / implementation named step (ai_collaborator_registry.ai_builder). No PM assignee user_started_at before first builder session started_at.',
    wire: 'existing',
  },
  {
    id: 'pm_touches_per_request',
    name: 'PM touches per request',
    group: 'coordination',
    headline: true,
    side: 'internal',
    tellsYou: 'Coordination load — one of the three economics proofs',
    formula: 'PM actions + PM messages / request',
    unit: 'ratio',
    sample: 2.1,
    grain: 'request',
    sources: ['data_ticket_user_assignees', 'data_mo_conversation_messages', 'data_assignation_logs'],
    calculate: `Count assignee status transitions and Mo/thread messages by ${PM_ROLES}, plus non-automated assignation_logs. Divide by completed+open requests in window.`,
    wire: 'proxy',
  },
  {
    id: 'human_handoffs_per_request',
    name: 'Human handoffs per request',
    group: 'coordination',
    tellsYou: 'Process complexity',
    formula: 'Actor transitions per request',
    unit: 'ratio',
    sample: 3.6,
    grain: 'request',
    sources: ['data_ticket_user_assignees', 'data_assignation_logs'],
    calculate: 'Count times the "current owner" changes: assignee user_role_id change, or step complete → next step different person. Exclude agent-to-agent Mo routing.',
    wire: 'existing',
  },
  {
    id: 'time_waiting_for_coordination',
    name: 'Time spent waiting for coordination',
    group: 'coordination',
    tellsYou: 'Non-development waste',
    formula: 'Time between a completed step and the next owner starting',
    unit: 'duration_ms',
    sample: 46800000,
    grain: 'step handoff',
    sources: ['data_ticket_user_assignees'],
    calculate: 'For consecutive steps on a ticket: next.user_started_at − prev.user_ended_at. Median and sum. This is average_handoff_latency aggregated.',
    wire: 'existing',
  },
  {
    id: 'auto_routed_work_rate',
    name: 'Auto-routed work rate',
    group: 'coordination',
    tellsYou: 'Mo orchestration value',
    formula: 'Steps assigned/routed without a human picker / steps assigned',
    unit: 'percent',
    sample: 0.69,
    grain: 'assignment',
    sources: ['data_assignation_queues', 'data_assignation_logs'],
    calculate: 'Numerator: queues with created_by=0 / triggered_by=daily_automated_reassignment, or logs is_automated=true. Denominator: all assignment events in window.',
    wire: 'existing',
  },
  {
    id: 'auto_created_work_rate',
    name: 'Auto-created work rate',
    group: 'coordination',
    tellsYou: 'Chat → workflow effectiveness',
    formula: 'Tickets/deliverables produced from requests automatically / work created',
    unit: 'percent',
    sample: 0.51,
    grain: 'ticket',
    sources: ['data_mo_tasks', 'data_tickets'],
    calculate: 'Tickets with data_mo_tasks.ticket_uuid set at creation. Same family as requests_created_without_manual_ticket_entry, at ticket grain.',
    wire: 'proxy',
  },
  {
    id: 'manual_ticket_edits_per_request',
    name: 'Manual ticket edits per request',
    group: 'coordination',
    tellsYou: 'Whether structured work needs repair',
    formula: 'Manual structural modifications / request',
    unit: 'ratio',
    sample: 1.4,
    grain: 'request',
    sources: ['data_tickets (updated_at vs created_at)', 'audit if present'],
    calculate: 'Count human updates to title, description, step, assignees after Mo creation, excluding status-only and agent writes. If no audit table, proxy: tickets where a PM edited within 2h of creation.',
    wire: 'needs_event',
    gaps: 'No clean structural-edit audit. Add event ticket_structurally_edited { actor_type, fields } when wiring.',
  },
  {
    id: 'clarification_rate',
    name: 'Clarification rate',
    group: 'coordination',
    tellsYou: 'Quality of understanding',
    formula: 'Requests requiring human clarification / requests',
    unit: 'percent',
    sample: 0.22,
    grain: 'request',
    sources: ['data_mo_objective_memories', 'data_mo_conversation_messages'],
    calculate: 'blockedOnUser=true or continuationIntent=wait_for_person or userClarification present, or Mo kind=approval/question back to the client before any implementation.',
    wire: 'existing',
  },
  {
    id: 'reopened_requests',
    name: 'Reopened requests',
    group: 'coordination',
    tellsYou: 'Whether work was prematurely considered done',
    formula: 'Reopened / completed requests',
    unit: 'percent',
    sample: 0.09,
    grain: 'request / ticket',
    sources: ['data_tickets.status', 'data_requests.status'],
    calculate: 'Ticket status returning from COMPLETED to IN_PROGRESS/NOT_STARTED (data_ticket.js already resets steps). Count distinct tickets that completed then left COMPLETED.',
    wire: 'existing',
  },
  {
    id: 'average_handoff_latency',
    name: 'Average handoff latency',
    group: 'coordination',
    tellsYou: 'How long transitions cost',
    formula: 'Median (next owner start − previous step complete)',
    unit: 'duration_ms',
    sample: 14400000,
    grain: 'step handoff',
    sources: ['data_ticket_user_assignees'],
    calculate: 'Same intervals as time_waiting_for_coordination; report median ms.',
    wire: 'existing',
  },
  {
    id: 'escalation_preparation_saved',
    name: 'Escalation preparation saved',
    group: 'coordination',
    tellsYou: 'AI groundwork before humans enter',
    formula: 'Share of escalations that already have Detective/planner evidence',
    unit: 'percent',
    sample: 0.82,
    grain: 'escalation',
    sources: ['data_agent_documents', 'data_ai_agent_runs'],
    calculate: 'Alias of context_preserved_escalation_rate at coordination grain. Keep both labels: this one is for internal ops, the other is the client-story headline.',
    wire: 'existing',
  },
  {
    id: 'concurrent_active_clients_per_pm',
    name: 'Concurrent active clients per PM',
    group: 'coordination',
    tellsYou: 'Huge business metric — service leverage',
    formula: 'Active maintained clients / PM FTE equivalents',
    unit: 'per_fte',
    sample: 6,
    grain: 'FTE',
    sources: ['data_user_roles', 'data_projects'],
    calculate: `PM FTE = distinct users with ${PM_ROLES} who took a PM action in the window (or billed PM seats). Divide active_clients. Sample 12 clients / 2 PM equivalents.`,
    wire: 'proxy',
    gaps: 'Need a PM-equivalent definition (headcount vs time-spent). Start with distinct PMs who touched work.',
  },
  {
    id: 'concurrent_active_projects_per_pm',
    name: 'Concurrent active projects per PM',
    group: 'coordination',
    tellsYou: 'Operational leverage',
    formula: 'Active projects / PM FTE',
    unit: 'per_fte',
    sample: 4.5,
    grain: 'FTE',
    sources: ['data_projects', 'data_requests'],
    calculate: 'Active project = status not archived and ≥1 non-completed request or Mo thread in window. Divide by PM FTE.',
    wire: 'existing',
  },
  {
    id: 'requests_per_pm',
    name: 'Requests handled per PM per month',
    group: 'coordination',
    headline: true,
    side: 'internal',
    tellsYou: 'Throughput leverage',
    formula: 'Completed client requests / PM FTE / month',
    unit: 'per_fte',
    sample: 38,
    grain: 'FTE',
    sources: ['data_requests', 'data_user_roles'],
    calculate: 'status=COMPLETED in window, annualize to 30 days, divide by PM FTE.',
    wire: 'existing',
  },

  // ── 3. Engineering ───────────────────────────────────────────────────────
  {
    id: 'developer_involvement_rate',
    name: 'Developer involvement rate',
    group: 'engineering',
    headline: true,
    side: 'internal',
    tellsYou: 'How much work still reaches humans',
    formula: 'Requests with a developer action / implementation requests',
    unit: 'percent',
    sample: 0.41,
    grain: 'request',
    sources: ['data_ticket_user_assignees', 'data_ai_chat_sessions', 'cat_roles'],
    calculate: 'Implementation request = has a Builder step or ai_builder collaborator. Developer action = TEAM member (not OWNER-only, not client) with user_started_at on a build/code step, or human_code_change (see gaps).',
    wire: 'proxy',
    gaps: 'Stamp actor_type=developer vs pm on assignee rows, or join step capability (coding vs PM).',
  },
  {
    id: 'developer_touches_per_request',
    name: 'Developer touches per request',
    group: 'engineering',
    headline: true,
    side: 'internal',
    tellsYou: 'Human interruption / workload',
    formula: 'Developer interactions per implementation request',
    unit: 'ratio',
    sample: 3.4,
    grain: 'request',
    sources: ['data_ticket_user_assignees', 'data_ai_chat_messages'],
    calculate: 'Count developer assignee start events + human messages on Builder sessions (role=user from a team member).',
    wire: 'proxy',
  },
  {
    id: 'developer_time_per_accepted_change',
    name: 'Developer time per accepted change',
    group: 'engineering',
    tellsYou: 'Most valuable engineering metric — attention per accepted outcome',
    formula: 'Human engineering time / accepted implementation outcomes',
    unit: 'duration_ms',
    sample: 7560000,
    grain: 'accepted request',
    sources: ['data_ticket_sections.real_time', 'data_ticket_user_assignees', 'WorkDurationService'],
    calculate: 'Sum real_time / schedule-aware work duration on human coding steps for requests that reached CHANGE_APPROVED or COMPLETED. Do not include AI Builder generating time.',
    wire: 'existing',
  },
  {
    id: 'human_hours_per_accepted_outcome',
    name: 'Human hours per accepted client outcome',
    group: 'engineering',
    tellsYou: 'Headline economics story with systems/engineer',
    formula: '(PM hours + developer hours) / accepted outcomes',
    unit: 'duration_ms',
    sample: 7560000,
    grain: 'accepted request',
    sources: ['data_ticket_user_assignees', 'data_ticket_sections'],
    calculate: 'Same duration source, include PM steps. Sample 2.1h after Motionode vs 6.5h before (SAMPLE_STRATEGIC).',
    wire: 'existing',
  },
  {
    id: 'investigation_time_before_coding',
    name: 'Investigation time before coding',
    group: 'engineering',
    tellsYou: 'How much Motionode saves developers',
    formula: 'First developer action − first implementation action, or Detective elapsed before Builder',
    unit: 'duration_ms',
    sample: 2400000,
    grain: 'request',
    sources: ['data_agent_sessions', 'data_ai_chat_sessions'],
    calculate: 'If a developer starts: developer user_started_at − min(detective session started_at). If no developer: Detective duration only (savings = that time the human did not spend).',
    wire: 'existing',
  },
  {
    id: 'ai_investigation_coverage',
    name: 'AI investigation coverage',
    group: 'engineering',
    tellsYou: 'Whether the developer starts with useful context',
    formula: 'Changes reaching a developer that already have Detective/agent evidence / those changes',
    unit: 'percent',
    sample: 0.78,
    grain: 'request that reached a developer',
    sources: ['data_agent_documents', 'data_ai_agent_runs'],
    calculate: 'Among requests with developer_involvement, fraction with a detective (or planner/security) document or run with technical_outcome=done before first developer start.',
    wire: 'existing',
  },
  {
    id: 'implementation_started_with_complete_context',
    name: 'Implementation started with complete context',
    group: 'engineering',
    tellsYou: 'Reduction in context reconstruction',
    formula: 'Requests where required evidence/spec existed before Builder or developer starts',
    unit: 'percent',
    sample: 0.63,
    grain: 'implementation request',
    sources: ['data_mo_objective_memories', 'data_agent_documents'],
    calculate: 'memory_json has investigation conclusions + ticket description/acceptance, and a specialist doc, before min(builder session created_at, developer start).',
    wire: 'proxy',
  },
  {
    id: 'time_to_first_implementation',
    name: 'Time to first implementation',
    group: 'engineering',
    tellsYou: 'Delivery speed to Build',
    formula: 'Accepted request → Build begins',
    unit: 'duration_ms',
    sample: 36000000,
    grain: 'request',
    sources: ['data_requests', 'data_ai_chat_sessions', 'data_tickets'],
    calculate: 'min(ai_chat_sessions.created_at, first build-step user_started_at) − request created_at (or client_bill_status first APPROVED if that is "accepted").',
    wire: 'existing',
  },
  {
    id: 'build_cycle_time',
    name: 'Build cycle time',
    group: 'engineering',
    tellsYou: 'Implementation speed',
    formula: 'Build begins → implementation complete',
    unit: 'duration_ms',
    sample: 28800000,
    grain: 'request',
    sources: ['data_ai_chat_sessions', 'data_mo_tasks'],
    calculate: 'Builder session deployed/sent_to_test/needs_revision terminal − generating start. Or mo_task building→built.',
    wire: 'existing',
  },
  {
    id: 'verification_cycle_time',
    name: 'Verification cycle time',
    group: 'engineering',
    tellsYou: 'QA speed',
    formula: 'Build complete → verification complete',
    unit: 'duration_ms',
    sample: 10800000,
    grain: 'request',
    sources: ['data_ai_verification_runs', 'data_mo_tasks'],
    calculate: 'Last verification_run ended_at (or qa session done) − implementation complete. mo_task verifying phase duration.',
    wire: 'existing',
  },
  {
    id: 'e2e_change_cycle_time',
    name: 'End-to-end change cycle time',
    group: 'engineering',
    tellsYou: 'Overall delivery speed',
    formula: 'Request accepted → ready/released',
    unit: 'duration_ms',
    sample: 207360000,
    grain: 'request',
    sources: ['data_requests', 'data_project_releases', 'data_project_activity_events'],
    calculate: 'CHANGE_RELEASED or data_project_releases completed − accepted (APPROVED bill status or request created if no gate). Median.',
    wire: 'existing',
  },
  {
    id: 'changes_completed_without_developer_coding',
    name: 'Changes completed without developer coding',
    group: 'engineering',
    tellsYou: 'AI implementation autonomy',
    formula: 'Accepted changes where Builder implemented and no human code edit occurred / accepted implementation changes',
    unit: 'percent',
    sample: 0.54,
    grain: 'accepted change',
    sources: ['data_ai_chat_sessions', 'data_ticket_user_assignees'],
    calculate: 'Builder reached deployed/sent_to_test AND no team-member user_started_at on the coding step. Human production approval does not count as coding.',
    wire: 'proxy',
    gaps: 'Need human_code_change vs ai_code_change events (git author, or Builder file writes vs IDE). Until then, assignee start on build step is the proxy.',
  },
  {
    id: 'ai_implementation_rate',
    name: 'AI implementation rate',
    group: 'engineering',
    headline: true,
    side: 'internal',
    tellsYou: 'Share of implementation done by Builder',
    formula: 'Same family as changes_completed_without_developer_coding',
    unit: 'percent',
    sample: 0.54,
    grain: 'accepted change',
    sources: ['data_ai_chat_sessions'],
    calculate: 'Headline alias of changes_completed_without_developer_coding.',
    wire: 'proxy',
  },
  {
    id: 'ai_implementation_acceptance_rate',
    name: 'AI implementation acceptance rate',
    group: 'engineering',
    tellsYou: 'Quality of Builder',
    formula: 'AI implementations accepted without human reimplementation / AI implementations',
    unit: 'percent',
    sample: 0.61,
    grain: 'Builder attempt',
    sources: ['data_ai_agent_runs', 'data_ai_run_feedback', 'data_ai_chat_sessions'],
    calculate: 'Builder runs with task_outcome accepted / all Builder implementation runs. Exclude those with feedback category=correction or session needs_revision then human takeover.',
    wire: 'existing',
  },
  {
    id: 'human_correction_rate',
    name: 'Human correction rate',
    group: 'engineering',
    tellsYou: 'How often developers must repair AI work',
    formula: 'AI changes requiring human code corrections / AI changes',
    unit: 'percent',
    sample: 0.27,
    grain: 'AI change',
    sources: ['data_ai_run_feedback', 'data_ai_chat_sessions.status'],
    calculate: 'needs_revision after Builder, or feedback category in (correction, bug_found), or a developer start after a Builder done on the same ticket.',
    wire: 'existing',
  },
  {
    id: 'human_takeover_rate',
    name: 'Human takeover rate',
    group: 'engineering',
    tellsYou: 'When Builder gives up',
    formula: 'AI implementations ultimately completed manually / attempted AI implementations',
    unit: 'percent',
    sample: 0.19,
    grain: 'Builder attempt',
    sources: ['data_ai_chat_sessions', 'data_agent_sessions'],
    calculate: 'Builder status=error or give-up, then a human completes the coding step. Distinct from correction (repair in place).',
    wire: 'proxy',
  },
  {
    id: 'average_ai_attempt_count',
    name: 'Average AI attempt count',
    group: 'engineering',
    tellsYou: 'Whether agents thrash',
    formula: 'Builder (or Detective) runs / completed changes',
    unit: 'ratio',
    sample: 2.4,
    grain: 'request',
    sources: ['data_ai_agent_runs'],
    calculate: 'COUNT runs where agent_key=ai_builder grouped by ticket/request, average. ChatGPT cost warning: $0.08/run × 17 runs is this KPI.',
    wire: 'existing',
  },
  {
    id: 'rework_rate',
    name: 'Rework rate',
    group: 'engineering',
    headline: true,
    side: 'internal',
    tellsYou: 'Quality of initial implementation',
    formula: 'Changes returned to Build after QA/review / changes',
    unit: 'percent',
    sample: 0.18,
    grain: 'change',
    sources: ['data_ai_chat_sessions', 'data_ai_verification_runs', 'data_tickets'],
    calculate: 'verification failed then a new Builder generating, or ticket reopened to a build step, or needs_revision. Denominator = implementation attempts that reached QA.',
    wire: 'existing',
  },
  {
    id: 'first_pass_verification_rate',
    name: 'First-pass verification rate',
    group: 'engineering',
    headline: true,
    side: 'internal',
    tellsYou: 'Strong quality metric',
    formula: 'Implementations passing verification on first attempt / implementations',
    unit: 'percent',
    sample: 0.67,
    grain: 'commit / ticket',
    sources: ['data_ai_verification_runs'],
    calculate: 'First verification_run per commit_sha (or ticket) with classification=fully_verified. Do not count later retries in the numerator.',
    wire: 'existing',
  },
  {
    id: 'developer_interruption_rate',
    name: 'Developer interruption rate',
    group: 'engineering',
    tellsYou: 'How much support work disrupts engineering',
    formula: 'Client questions requiring a developer / client questions',
    unit: 'percent',
    sample: 0.11,
    grain: 'question',
    sources: ['data_mo_conversation_messages', 'data_ticket_user_assignees'],
    calculate: 'Client question whose ticket then has a developer assignee start within N hours, or escalate_human to a coding role.',
    wire: 'proxy',
  },
  {
    id: 'developer_questions_avoided',
    name: 'Developer questions avoided',
    group: 'engineering',
    tellsYou: 'Support investigations answered without a developer',
    formula: 'Questions AI/PM resolves without developer involvement / client questions',
    unit: 'percent',
    sample: 0.89,
    grain: 'question',
    sources: ['data_mo_conversation_messages'],
    calculate: 'Complement of developer_interruption_rate.',
    wire: 'proxy',
  },
  {
    id: 'prs_per_developer',
    name: 'PRs / accepted changes per developer',
    group: 'engineering',
    tellsYou: 'General throughput',
    formula: 'Accepted changes / developer FTE',
    unit: 'per_fte',
    sample: 6.2,
    grain: 'FTE',
    sources: ['data_tickets.pr_url_for_test', 'data_ai_chat_sessions', 'data_user_roles'],
    calculate: 'Count tickets with pr_url or Builder deployed, divide by distinct team members who coded in the window.',
    wire: 'existing',
  },
  {
    id: 'production_changes_per_developer',
    name: 'Production changes per developer',
    group: 'engineering',
    tellsYou: 'Outcome throughput',
    formula: 'Released changes / developer FTE',
    unit: 'per_fte',
    sample: 4.1,
    grain: 'FTE',
    sources: ['data_project_releases', 'data_ticket_deploy_events'],
    calculate: 'Releases with env_kind=production and status completed / developer FTE.',
    wire: 'existing',
  },
  {
    id: 'active_systems_per_developer',
    name: 'Active systems maintained per developer',
    group: 'engineering',
    tellsYou: 'Most strategic engineering metric — the 2.4 → 6.8 story',
    formula: 'Active production systems / engineering FTE',
    unit: 'per_fte',
    sample: 6.8,
    grain: 'FTE',
    sources: ['data_projects', 'data_project_services', 'data_user_roles'],
    calculate: 'Count production-live services (or projects with a production deploy in last 90d) / engineering FTE. Sample uses SAMPLE_STRATEGIC.after. Do not publish until real.',
    wire: 'proxy',
    gaps: 'Define "system": project vs production service vs Motinode env. Recommend data_project_services with a production environment row.',
  },
  {
    id: 'completed_outcomes_per_delivery_fte',
    name: 'Completed client outcomes per delivery FTE',
    group: 'engineering',
    tellsYou: 'The other half of the leverage story',
    formula: 'Accepted client outcomes / (PM + engineer) FTE',
    unit: 'per_fte',
    sample: 22,
    grain: 'FTE',
    sources: ['data_requests', 'data_user_roles'],
    calculate: 'COMPLETED requests in window / delivery FTE. Pair with human_hours_per_accepted_outcome and active_systems_per_developer.',
    wire: 'existing',
  },

  // ── 4. Autonomy (layered — never one generic %) ──────────────────────────
  {
    id: 'questions_resolved_autonomously',
    name: 'Questions resolved autonomously',
    group: 'autonomy',
    tellsYou: 'No human needed for the question',
    formula: 'Same as AI question resolution rate',
    unit: 'percent',
    sample: 0.71,
    grain: 'question',
    sources: ['data_mo_conversation_messages', 'data_mo_objective_memories'],
    calculate: 'Layer: investigation/Q&A autonomy. Mandatory approvals do not count as intervention.',
    wire: 'proxy',
  },
  {
    id: 'investigations_completed_autonomously',
    name: 'Investigations completed autonomously',
    group: 'autonomy',
    tellsYou: 'Detective reached a useful conclusion without a human',
    formula: 'Detective runs with task_outcome done and no awaiting_user / Detective runs',
    unit: 'percent',
    sample: 0.66,
    grain: 'Detective run, rolled to request',
    sources: ['data_agent_sessions', 'data_ai_agent_runs'],
    calculate: 'collaborator_key=detective, status=done, not awaiting_user, signals.root_cause_found or document present. Roll up: request had ≥1 such run.',
    wire: 'existing',
  },
  {
    id: 'plans_produced_autonomously',
    name: 'Plans produced autonomously',
    group: 'autonomy',
    tellsYou: 'No PM had to reconstruct the plan',
    formula: 'Planner (or Mo) plans accepted without PM rewrite / plans',
    unit: 'percent',
    sample: 0.58,
    grain: 'request',
    sources: ['data_agent_sessions', 'data_agent_documents', 'data_mo_tasks'],
    calculate: 'planner collaborator done + document, and no PM structural ticket edit before Build.',
    wire: 'proxy',
  },
  {
    id: 'work_organized_autonomously',
    name: 'Work organized autonomously',
    group: 'autonomy',
    tellsYou: 'Ticket/steps/ownership generated correctly',
    formula: 'Mo-created tickets that were not structurally repaired / Mo-created tickets',
    unit: 'percent',
    sample: 0.55,
    grain: 'ticket',
    sources: ['data_mo_tasks', 'data_tickets'],
    calculate: 'Inverse of manual_ticket_edits on Mo-origin tickets.',
    wire: 'proxy',
  },
  {
    id: 'changes_implemented_autonomously',
    name: 'Changes implemented autonomously',
    group: 'autonomy',
    tellsYou: 'Builder made the implementation',
    formula: 'Same as AI implementation rate — coding only, not end-to-end',
    unit: 'percent',
    sample: 0.54,
    grain: 'implementation request',
    sources: ['data_ai_chat_sessions'],
    calculate: 'AI implementation autonomy layer. A required production approval still counts as autonomous implementation.',
    wire: 'proxy',
  },
  {
    id: 'changes_verified_autonomously',
    name: 'Changes verified autonomously',
    group: 'autonomy',
    tellsYou: 'Verification completed without human QA',
    formula: 'QA/verification passed with no human QA assignee start / verifications',
    unit: 'percent',
    sample: 0.49,
    grain: 'verification',
    sources: ['data_ai_verification_runs', 'data_agent_sessions'],
    calculate: 'qa collaborator or verification_run passed, no TEAM assignee on the test step.',
    wire: 'existing',
  },
  {
    id: 'requests_fully_completed_autonomously',
    name: 'Requests fully completed autonomously',
    group: 'autonomy',
    tellsYou: 'No human action from request to accepted outcome, excluding policy-required approvals',
    formula: 'Requests with zero rescue/coordination touches / completed requests',
    unit: 'percent',
    sample: 0.17,
    grain: 'request',
    sources: ['data_mo_objective_memories', 'data_ticket_user_assignees'],
    calculate: 'End-to-end autonomy layer. Exclude mandatory production approval and client preview approve. Include as intervention: PM routing, developer code, founder rescue, escalate_human.',
    wire: 'proxy',
  },
  {
    id: 'requests_completed_with_only_approval',
    name: 'Requests completed with only approval',
    group: 'autonomy',
    tellsYou: 'AI did everything operational; human/client only authorized',
    formula: 'Completed requests whose only human acts were policy approvals / completed',
    unit: 'percent',
    sample: 0.23,
    grain: 'request',
    sources: ['data_ai_chat_messages.approval_status', 'data_project_activity_events'],
    calculate: 'Human actors only appear on approval_granted / CHANGE_APPROVED / production approve. No coding, no PM ticket repair.',
    wire: 'proxy',
  },
  {
    id: 'requests_requiring_intervention',
    name: 'Requests requiring intervention',
    group: 'autonomy',
    tellsYou: 'Human had to repair, reason, or execute',
    formula: 'Requests with a human rescue / completed+open implementation requests',
    unit: 'percent',
    sample: 0.6,
    grain: 'request',
    sources: ['data_mo_objective_memories', 'data_ai_chat_sessions'],
    calculate: 'escalate_human, needs_revision, developer start, or founder rescue. Opposite of fully autonomous + only-approval.',
    wire: 'proxy',
  },
  {
    id: 'founder_rescue_rate',
    name: 'Founder-rescue rate',
    group: 'autonomy',
    headline: true,
    side: 'internal',
    tellsYou: 'Pilot reality — a named human had to save the run',
    formula: 'Outcomes where an OWNER/team lead intervened after AI failure / outcomes',
    unit: 'percent',
    sample: 0.11,
    grain: 'request',
    sources: ['data_ai_agent_runs', 'data_mo_tasks', 'data_user_roles'],
    calculate: 'No founder_rescue column. Proxy: after status=error or task_outcome=rejected on the ticket, an OWNER or TEAM_LEADER takes an action within 24h (message, assignee start, or Mo handoff). Tag those users later if you want a literal founder list.',
    wire: 'proxy',
    gaps: 'Add event human_rescue { actor_id, after_error: true } to make this credible.',
  },
  {
    id: 'agent_give_up_rate',
    name: 'Agent give-up rate',
    group: 'autonomy',
    tellsYou: 'Agent explicitly concluded it could not continue',
    formula: 'Runs that terminate as give-up / runs',
    unit: 'percent',
    sample: 0.08,
    grain: 'agent run',
    sources: ['agent_give_up_resume_service', 'data_agent_sessions', 'data_ai_agent_runs'],
    calculate: 'Tool-loop give-up / partial report, or session awaiting_user with a give-up document. Distinct from timeout and budget exhaustion.',
    wire: 'existing',
  },
  {
    id: 'agent_false_success_rate',
    name: 'Agent false-success rate',
    group: 'autonomy',
    tellsYou: 'Claimed completion contradicted by evidence',
    formula: 'Runs marked done whose verification/task_outcome failed / runs marked done',
    unit: 'percent',
    sample: 0.06,
    grain: 'agent run',
    sources: ['data_ai_agent_runs', 'data_ai_verification_runs'],
    calculate: 'session status=done AND (technical_outcome=failed OR verification not fully_verified OR feedback rejected). This is not a runtime crash.',
    wire: 'existing',
  },
  {
    id: 'tool_runtime_failure_rate',
    name: 'Tool / runtime failure rate',
    group: 'autonomy',
    tellsYou: 'System failed rather than reasoning failed — do not "fix prompts" for this',
    formula: 'Runs/calls with infrastructure error / runs',
    unit: 'percent',
    sample: 0.09,
    grain: 'agent run / model call',
    sources: ['data_ai_agent_runs.error_class', 'data_ai_model_calls', 'data_ai_preview_instances'],
    calculate: 'error_class in timeout/network/tool/preview_error/deploy failed — not task_outcome=rejected. Split this from agent_false_success_rate on the dashboard.',
    wire: 'existing',
  },

  // ── 5. Reliability (own tab) ─────────────────────────────────────────────
  {
    id: 'successful_agent_completion_rate',
    name: 'Successful agent completion rate',
    group: 'reliability',
    tellsYou: 'Basic reliability',
    formula: 'Runs with status=done and technical_outcome success / runs that finished',
    unit: 'percent',
    sample: 0.81,
    grain: 'agent run (reliability grain — OK here)',
    sources: ['data_ai_agent_runs'],
    calculate: 'status=done, technical_outcome not failed. Filter by agent_key for Detective vs Builder.',
    wire: 'existing',
  },
  {
    id: 'runtime_failure_rate',
    name: 'Runtime failure rate',
    group: 'reliability',
    headline: true,
    side: 'internal',
    tellsYou: 'Infrastructure stability',
    formula: 'Runs with infrastructure error / finished runs',
    unit: 'percent',
    sample: 0.09,
    grain: 'agent run',
    sources: ['data_ai_agent_runs', 'data_ai_preview_instances', 'data_ticket_deploy_events'],
    calculate: 'Same as tool_runtime_failure_rate; headline uses this name.',
    wire: 'existing',
  },
  {
    id: 'timeout_rate',
    name: 'Timeout rate',
    group: 'reliability',
    tellsYou: 'Whether work routinely exhausts time',
    formula: 'Timeouts / runs',
    unit: 'percent',
    sample: 0.04,
    grain: 'agent run',
    sources: ['data_ai_agent_runs.error_class', 'agent_tool_loop_service'],
    calculate: 'error_class timeout or duration_ms hitting the investigation/tool budget wall without a done result.',
    wire: 'existing',
  },
  {
    id: 'budget_exhaustion_rate',
    name: 'Budget exhaustion rate',
    group: 'reliability',
    tellsYou: 'Credits/tokens/time budget stopped the agent',
    formula: 'Budget-terminal runs / runs',
    unit: 'percent',
    sample: 0.07,
    grain: 'agent run',
    sources: ['agent_tool_loop_service stop reasons', 'data_ai_agent_runs'],
    calculate: 'token_budget_reached, call_budget_reached, soft_budget_reached, INVESTIGATION_BUDGET_EXHAUSTED, Mo no_credits / evaluateBudgetGate.',
    wire: 'existing',
  },
  {
    id: 'cancellation_success_rate',
    name: 'Cancellation success rate',
    group: 'reliability',
    tellsYou: 'Stop must actually stop',
    formula: 'Cancels that reached aborted / cancel requested',
    unit: 'percent',
    sample: 0.93,
    grain: 'agent run',
    sources: ['agent_run_bus', 'data_ai_agent_runs.status'],
    calculate: 'user_cancellation / abort requested vs status=aborted within a timeout. Pair request+complete events if you add cancel_requested / cancel_completed.',
    wire: 'proxy',
    gaps: 'Emit cancel_requested and cancel_completed if the bus does not already persist both.',
  },
  {
    id: 'evidence_preservation_rate',
    name: 'Evidence preservation rate',
    group: 'reliability',
    tellsYou: 'Critical given recent Detective→Mo remainder bugs',
    formula: 'Handoffs/wakes where specialist remaining work is present in Mo memory / handoffs',
    unit: 'percent',
    sample: 0.74,
    grain: 'handoff',
    sources: ['data_mo_objective_memories', 'data_agent_documents', 'data_agent_messages'],
    calculate: 'After a Detective done, memory_json has specialistRemaining / findings, or a document row exists, and Mo synthesis is not empty. False = remainder never left the specialist chat.',
    wire: 'proxy',
  },
  {
    id: 'final_answer_completion_rate',
    name: 'Final-answer completion rate',
    group: 'reliability',
    tellsYou: 'Agent finishes with usable synthesis',
    formula: 'Runs that produced a client-visible answer / runs that should have',
    unit: 'percent',
    sample: 0.77,
    grain: 'Mo / specialist run',
    sources: ['data_mo_conversation_messages', 'data_ai_agent_runs'],
    calculate: 'Assistant kind=text|summary after a run_uuid, not empty, not kind=error. For specialists: document or activity_event completed with a report.',
    wire: 'existing',
  },
  {
    id: 'delegation_failure_rate',
    name: 'Delegation failure rate',
    group: 'reliability',
    tellsYou: 'Subagent reliability',
    formula: 'Failed child runs / delegated child runs',
    unit: 'percent',
    sample: 0.14,
    grain: 'agent run',
    sources: ['data_ai_agent_runs', 'data_agent_messages askedBy=agent'],
    calculate: 'Parent Mo/Detective run with child source; child status=error or aborted. Count askedBy=agent org questions left open.',
    wire: 'existing',
  },
  {
    id: 'tool_failure_rate_by_tool',
    name: 'Tool failure rate by tool',
    group: 'reliability',
    tellsYou: 'Finds broken integrations',
    formula: 'Failed tool calls / calls, grouped by tool name',
    unit: 'percent',
    sample: 0.12,
    grain: 'tool call',
    sources: ['data_ai_chat_messages (tool role)', 'agent tool loop logs', 'data_ai_model_calls'],
    calculate: 'Parse tool error rows from Builder tool messages and collaborator tool loop. Group by tool name (db, git, preview, browser). Sample is an overall blend — UI table should break down by tool.',
    wire: 'proxy',
    gaps: 'Best as a table, not one number. Persist tool name + ok on model/tool call rows if not already.',
  },
  {
    id: 'retries_per_successful_run',
    name: 'Retries per successful run',
    group: 'reliability',
    tellsYou: 'Hidden cost / reliability',
    formula: 'Extra model calls or run retries / successful outcomes',
    unit: 'ratio',
    sample: 1.8,
    grain: 'outcome',
    sources: ['data_ai_model_calls', 'data_ai_agent_runs'],
    calculate: '(total model calls / successful parent outcomes) − 1, or COUNT retries where selected_reason starts with escalation_.',
    wire: 'existing',
  },
  {
    id: 'avg_model_calls_per_outcome',
    name: 'Average model calls per outcome',
    group: 'reliability',
    tellsYou: 'Complexity / cost',
    formula: 'Model calls / accepted client outcomes',
    unit: 'ratio',
    sample: 11.4,
    grain: 'request',
    sources: ['data_ai_model_calls', 'data_ai_agent_runs.ticket_id'],
    calculate: 'Join calls → runs → ticket → request. This is the "17 DeepSeek calls" metric.',
    wire: 'existing',
  },
  {
    id: 'avg_delegate_count_per_outcome',
    name: 'Average delegate count per outcome',
    group: 'reliability',
    tellsYou: 'Whether agents over-orchestrate',
    formula: 'Specialist sessions / outcome',
    unit: 'ratio',
    sample: 2.7,
    grain: 'request',
    sources: ['data_agent_sessions', 'data_ai_agent_runs'],
    calculate: 'COUNT distinct (collaborator_key, session) per request. Mo + Detective + Builder = 3.',
    wire: 'existing',
  },
  {
    id: 'environment_mismatch_incidents',
    name: 'Environment mismatch incidents',
    group: 'reliability',
    tellsYou: 'Staging vs production confusion',
    formula: 'Count of mismatched env actions',
    unit: 'count',
    sample: 3,
    grain: 'incident',
    sources: ['data_ticket_deploy_events', 'data_ai_preview_instances.mode', 'data_ai_chat_sessions'],
    calculate: 'Production deploy without staging_verified_at, or preview mode=production when ticket asked staging, or browser-check warnings. Start as a counted incident list.',
    wire: 'needs_event',
    gaps: 'Add environment_mismatch when deploy env_kind ≠ intended ticket env.',
  },
  {
    id: 'false_completion_rate',
    name: 'False completion / false green rate',
    group: 'reliability',
    tellsYou: 'Extremely important — claimed done vs actually done',
    formula: 'Same family as agent_false_success_rate plus verification unable_to_reproduce marked passed',
    unit: 'percent',
    sample: 0.05,
    grain: 'agent run / verification',
    sources: ['data_ai_verification_runs.classification', 'data_ai_agent_runs'],
    calculate: 'classification=unable_to_reproduce treated as green, or task_outcome success contradicted by later reopen/hotfix.',
    wire: 'existing',
  },
  {
    id: 'unable_to_reproduce_rate',
    name: 'Unable-to-reproduce rate',
    group: 'reliability',
    tellsYou: 'Verification quality',
    formula: 'verification classification=unable_to_reproduce / verification runs',
    unit: 'percent',
    sample: 0.08,
    grain: 'verification',
    sources: ['data_ai_verification_runs'],
    calculate: 'Direct column. High rate means QA is not actually checking the change.',
    wire: 'existing',
  },
  {
    id: 'human_correction_after_ai_conclusion',
    name: 'Human correction after AI conclusion',
    group: 'reliability',
    tellsYou: 'Real-world reliability',
    formula: 'AI done followed by human correction / AI done',
    unit: 'percent',
    sample: 0.21,
    grain: 'request',
    sources: ['data_ai_run_feedback', 'data_tickets'],
    calculate: 'After a done run, feedback correction or ticket reopen or needs_revision. Broader than human_correction_rate (includes product, not only code).',
    wire: 'existing',
  },

  // ── 6. Delivery quality ──────────────────────────────────────────────────
  {
    id: 'first_pass_qa_success',
    name: 'First-pass QA success',
    group: 'quality',
    tellsYou: 'Change passes first verification',
    formula: 'Alias of first_pass_verification_rate',
    unit: 'percent',
    sample: 0.67,
    grain: 'implementation',
    sources: ['data_ai_verification_runs'],
    calculate: 'Same as first_pass_verification_rate.',
    wire: 'existing',
  },
  {
    id: 'client_first_review_acceptance',
    name: 'Client first-review acceptance',
    group: 'quality',
    headline: true,
    side: 'external',
    tellsYou: 'Client approves preview without another iteration',
    formula: 'First CHANGE_APPROVED with no prior FEEDBACK then rebuild / previews shown',
    unit: 'percent',
    sample: 0.58,
    grain: 'request',
    sources: ['data_project_activity_events'],
    calculate: 'Per request: first FEEDBACK_REQUESTED or PREVIEW_READY followed by CHANGE_APPROVED without another PREVIEW_READY in between.',
    wire: 'existing',
  },
  {
    id: 'review_iterations_per_change',
    name: 'Review iterations per change',
    group: 'quality',
    tellsYou: 'Rework in review',
    formula: 'Preview/feedback cycles / change',
    unit: 'ratio',
    sample: 1.7,
    grain: 'request',
    sources: ['data_project_activity_events', 'data_ai_preview_instances'],
    calculate: 'Count PREVIEW_READY (or preview instance created) per request. Average.',
    wire: 'existing',
  },
  {
    id: 'reopened_ticket_rate',
    name: 'Reopened ticket rate',
    group: 'quality',
    tellsYou: 'Outcome was not actually complete',
    formula: 'Reopened tickets / completed tickets',
    unit: 'percent',
    sample: 0.09,
    grain: 'ticket',
    sources: ['data_tickets'],
    calculate: 'Same signal as reopened_requests at ticket grain.',
    wire: 'existing',
  },
  {
    id: 'post_release_defect_rate',
    name: 'Post-release defect rate',
    group: 'quality',
    tellsYou: 'Quality escaped verification',
    formula: 'Defects linked to a release within N days / releases',
    unit: 'percent',
    sample: 0.12,
    grain: 'release',
    sources: ['data_tickets (hotfix/interrupt)', 'data_project_releases'],
    calculate: 'Interrupt or discovery tickets created within 14d of a production release on the same project, or tickets tagged hotfix. Weak until you stamp caused_by_release_id.',
    wire: 'needs_event',
    gaps: 'Add ticket.caused_by_release_id or event post_release_defect.',
  },
  {
    id: 'rollback_rate',
    name: 'Rollback rate',
    group: 'quality',
    tellsYou: 'Bad production releases',
    formula: 'Rollbacks / production releases',
    unit: 'percent',
    sample: 0.03,
    grain: 'release',
    sources: ['data_ticket_deploy_events', 'data_project_releases'],
    calculate: 'Deploy events that revert a sha, or release status failed after completed. May need a rollback flag.',
    wire: 'needs_event',
  },
  {
    id: 'hotfix_rate',
    name: 'Hotfix rate',
    group: 'quality',
    tellsYou: 'Release quality',
    formula: 'Hotfix tickets / production releases',
    unit: 'percent',
    sample: 0.08,
    grain: 'release',
    sources: ['data_tickets.is_interrupt', 'data_requests.is_interrupt'],
    calculate: 'is_interrupt tickets after a production deploy on the same service/project. Proxy until hotfix is explicit.',
    wire: 'proxy',
  },
  {
    id: 'failed_deployment_rate',
    name: 'Failed deployment rate',
    group: 'quality',
    tellsYou: 'Deployment reliability',
    formula: 'Deploy events status=failed / deploy events',
    unit: 'percent',
    sample: 0.06,
    grain: 'deploy',
    sources: ['data_ticket_deploy_events', 'data_project_releases'],
    calculate: 'status=failed on deploy events or releases. Split staging vs production via env_kind.',
    wire: 'existing',
  },
  {
    id: 'time_from_defect_discovery_to_fix',
    name: 'Time from defect discovery to fix',
    group: 'quality',
    tellsYou: 'Recovery speed',
    formula: 'Median (fix release − defect ticket created)',
    unit: 'duration_ms',
    sample: 86400000,
    grain: 'defect ticket',
    sources: ['data_tickets', 'data_project_releases'],
    calculate: 'Interrupt/hotfix ticket created_at → its COMPLETED or related production release.',
    wire: 'proxy',
  },
  {
    id: 'regression_rate',
    name: 'Regression rate',
    group: 'quality',
    tellsYou: 'Existing behavior broken',
    formula: 'Verified regressions / released changes',
    unit: 'percent',
    sample: 0.07,
    grain: 'release',
    sources: ['data_ai_verification_runs', 'data_ai_run_feedback'],
    calculate: 'QA/verification notes regression, or feedback category bug_found on a previously passing area. Needs a classification later.',
    wire: 'needs_event',
  },
  {
    id: 'acceptance_criteria_pass_rate',
    name: 'Acceptance-criteria pass rate',
    group: 'quality',
    tellsYou: 'Alignment with the request',
    formula: 'Outcomes whose acceptance criteria passed / outcomes with criteria',
    unit: 'percent',
    sample: 0.72,
    grain: 'request',
    sources: ['data_ai_verification_runs', 'ticket description / Mo memory'],
    calculate: 'verification fully_verified AND client CHANGE_APPROVED. If criteria live in ticket description only, this stays a proxy.',
    wire: 'proxy',
  },
  {
    id: 'request_to_accepted_outcome_rate',
    name: 'Change request → accepted outcome rate',
    group: 'quality',
    tellsYou: 'Ultimate delivery success',
    formula: 'Accepted/completed requests / requests that left draft',
    unit: 'percent',
    sample: 0.81,
    grain: 'request',
    sources: ['data_requests'],
    calculate: 'status=COMPLETED or CHANGE_APPROVED / requests with status QUEUED or COMPLETED (exclude abandoned UNDEFINED).',
    wire: 'existing',
  },

  // ── 7. Preview ───────────────────────────────────────────────────────────
  {
    id: 'preview_generation_success_rate',
    name: 'Preview generation success rate',
    group: 'preview',
    tellsYou: 'Reliability of getting a client-visible result',
    formula: 'Instances reaching status=ready / start attempts',
    unit: 'percent',
    sample: 0.84,
    grain: 'preview instance',
    sources: ['data_ai_preview_instances'],
    calculate: 'status=ready / rows created. Failed/expired without ready count as failures.',
    wire: 'existing',
  },
  {
    id: 'time_to_preview',
    name: 'Time to preview',
    group: 'preview',
    headline: true,
    side: 'external',
    tellsYou: 'Request/Build → client-visible result',
    formula: 'Median (preview ready − request accepted or build start)',
    unit: 'duration_ms',
    sample: 11160000,
    grain: 'request',
    sources: ['data_ai_preview_instances', 'data_project_activity_events', 'data_ai_chat_sessions'],
    calculate: 'PREVIEW_READY event or instance status=ready timestamp − CHANGE_STARTED or Builder start. Prefer activity events for the client funnel.',
    wire: 'existing',
  },
  {
    id: 'preview_open_rate',
    name: 'Preview open rate',
    group: 'preview',
    tellsYou: 'Whether clients actually use it',
    formula: 'Previews with an open / previews ready',
    unit: 'percent',
    sample: 0.71,
    grain: 'preview',
    sources: ['data_ai_preview_instances.last_activity_at'],
    calculate: 'Proxy: last_activity_at after ready. Not a real signed-link open log.',
    wire: 'needs_event',
    gaps: 'No mnpt-open table. Add preview_opened { instance_uuid, actor } from the preview proxy or share-email click.',
  },
  {
    id: 'preview_feedback_rate',
    name: 'Preview feedback rate',
    group: 'preview',
    tellsYou: 'Engagement',
    formula: 'FEEDBACK_REQUESTED that received a client response / previews',
    unit: 'percent',
    sample: 0.46,
    grain: 'preview',
    sources: ['data_project_activity_events'],
    calculate: 'FEEDBACK_REQUESTED followed by client actor_role_bucket=CLIENT event or CHANGE_APPROVED/comment.',
    wire: 'existing',
  },
  {
    id: 'preview_approval_rate',
    name: 'Preview approval rate',
    group: 'preview',
    tellsYou: 'Outcome quality of the preview',
    formula: 'CHANGE_APPROVED / previews that were reviewed',
    unit: 'percent',
    sample: 0.64,
    grain: 'request',
    sources: ['data_project_activity_events'],
    calculate: 'CHANGE_APPROVED / (CHANGE_APPROVED + rejected/feedback-without-approve).',
    wire: 'existing',
  },
  {
    id: 'preview_revision_count',
    name: 'Preview revision count',
    group: 'preview',
    tellsYou: 'Rework in preview',
    formula: 'Average preview instances (or PREVIEW_READY events) per request',
    unit: 'ratio',
    sample: 1.6,
    grain: 'request',
    sources: ['data_ai_preview_instances', 'data_project_activity_events'],
    calculate: 'COUNT instances per ticket/request. Same idea as review_iterations_per_change.',
    wire: 'existing',
  },
  {
    id: 'time_from_preview_to_approval',
    name: 'Time from preview to approval',
    group: 'preview',
    tellsYou: 'Client decision latency',
    formula: 'Median (CHANGE_APPROVED − PREVIEW_READY)',
    unit: 'duration_ms',
    sample: 46800000,
    grain: 'request',
    sources: ['data_project_activity_events'],
    calculate: 'Pair last PREVIEW_READY before CHANGE_APPROVED.',
    wire: 'existing',
  },
  {
    id: 'changes_released_after_preview',
    name: 'Changes released after preview',
    group: 'preview',
    tellsYou: 'Preview → production funnel',
    formula: 'CHANGE_RELEASED among requests that had PREVIEW_READY / those requests',
    unit: 'percent',
    sample: 0.52,
    grain: 'request',
    sources: ['data_project_activity_events', 'data_project_releases'],
    calculate: 'Funnel conversion PREVIEW_READY → CHANGE_RELEASED.',
    wire: 'existing',
  },
  {
    id: 'preview_infrastructure_failure_rate',
    name: 'Preview infrastructure failure rate',
    group: 'preview',
    tellsYou: 'Technical health of pv-* / mnpt',
    formula: 'Instances failed or 401/boot errors / start attempts',
    unit: 'percent',
    sample: 0.11,
    grain: 'preview instance',
    sources: ['data_ai_preview_instances', 'data_ai_chat_sessions.preview_error'],
    calculate: 'status=failed or preview_error set. Distinct from "client did not like the UI".',
    wire: 'existing',
  },
  {
    id: 'time_to_release',
    name: 'Time to release',
    group: 'preview',
    headline: true,
    side: 'external',
    tellsYou: 'Accepted work → production',
    formula: 'Median (release completed − request accepted)',
    unit: 'duration_ms',
    sample: 207360000,
    grain: 'request',
    sources: ['data_project_releases', 'data_project_activity_events'],
    calculate: 'Same clock as e2e_change_cycle_time; headline uses this label.',
    wire: 'existing',
  },

  // ── 8. Economics ─────────────────────────────────────────────────────────
  {
    id: 'ai_cost_per_client_question',
    name: 'AI cost per client question',
    group: 'economics',
    tellsYou: 'Cost to service a question — not cost per model call',
    formula: 'SUM(usd_cost) of Mo+Detective runs on question outcomes / questions',
    unit: 'usd',
    sample: 0.42,
    grain: 'question',
    sources: ['data_ai_agent_runs', 'data_credit_ledger'],
    calculate: 'Join runs with surface=mo_chat or detective on threads that did not create tickets. Use usd_cost (not credits — credits include Mo 3×).',
    wire: 'existing',
  },
  {
    id: 'ai_cost_per_investigation',
    name: 'AI cost per investigation',
    group: 'economics',
    tellsYou: 'Detective economics',
    formula: 'SUM usd_cost where agent_key=detective / detective outcomes',
    unit: 'usd',
    sample: 1.18,
    grain: 'investigation (ticket/request)',
    sources: ['data_ai_agent_runs'],
    calculate: 'Group Detective runs by ticket_id. Include research delegates on that ticket.',
    wire: 'existing',
  },
  {
    id: 'ai_cost_per_accepted_change',
    name: 'AI cost per accepted change',
    group: 'economics',
    tellsYou: 'Much more important than cost/run',
    formula: 'SUM usd_cost of all agents on the request / accepted changes',
    unit: 'usd',
    sample: 9.4,
    grain: 'request',
    sources: ['data_ai_agent_runs', 'data_credit_ledger'],
    calculate: 'Roll every run on tickets of the request, including retries. This is the "17 DeepSeek calls" total, not $0.08.',
    wire: 'existing',
  },
  {
    id: 'ai_cost_per_released_change',
    name: 'AI cost per released change',
    group: 'economics',
    tellsYou: 'Full delivery AI economics',
    formula: 'SUM usd_cost / production releases',
    unit: 'usd',
    sample: 14.1,
    grain: 'release',
    sources: ['data_ai_agent_runs', 'data_project_releases'],
    calculate: 'Same as accepted, only for requests that reached CHANGE_RELEASED.',
    wire: 'existing',
  },
  {
    id: 'total_variable_cost_per_request',
    name: 'Total variable cost per request',
    group: 'economics',
    tellsYou: 'Models + compute + retries + relevant support AI',
    formula: 'AI usd + estimated preview/compute if billed + retry waste',
    unit: 'usd',
    sample: 11.2,
    grain: 'request',
    sources: ['data_ai_agent_runs', 'data_credit_ledger', 'data_pipeline_runs'],
    calculate: 'usd_cost + any Motinode compute you later attribute. For now AI only.',
    wire: 'existing',
  },
  {
    id: 'human_cost_per_request',
    name: 'Human cost per request',
    group: 'economics',
    tellsYou: 'Estimated human time × loaded cost',
    formula: 'Hours × loaded hourly rate',
    unit: 'usd',
    sample: 86,
    grain: 'request',
    sources: ['data_ticket_user_assignees', 'org loaded-cost config (not in product yet)'],
    calculate: 'human_hours_per_accepted_outcome × a configured $/h (PM vs engineer). Store the rate in admin settings when you wire this.',
    wire: 'needs_event',
    gaps: 'No loaded-cost table. Add org setting loaded_hourly_rate_pm / _engineer.',
  },
  {
    id: 'cost_per_accepted_outcome',
    name: 'Total delivery cost per accepted outcome',
    group: 'economics',
    headline: true,
    side: 'internal',
    tellsYou: 'The investor metric — AI + human',
    formula: 'AI variable + human cost / accepted outcomes',
    unit: 'usd',
    sample: 14.8,
    grain: 'request',
    sources: ['data_ai_agent_runs', 'data_ticket_user_assignees'],
    calculate: 'Sample is AI-only-ish until human rates exist; when wired, sum both. Never report cost per model call as the headline.',
    wire: 'proxy',
  },
  {
    id: 'gross_margin_per_client',
    name: 'Gross margin per client / project',
    group: 'economics',
    tellsYou: 'Commercial viability',
    formula: '(Revenue − delivery cost) / revenue',
    unit: 'percent',
    sample: 0.61,
    grain: 'project / client org',
    sources: ['billing', 'data_credit_ledger', 'human cost'],
    calculate: 'Need client revenue (invoices / retainers) minus cost_per_accepted_outcome rolled to the client. Admin billing tables already exist for Motionode SaaS — Advante client revenue may be a manual overlay at first.',
    wire: 'needs_event',
  },
  {
    id: 'model_cost_as_pct_revenue',
    name: 'Model cost as % revenue',
    group: 'economics',
    tellsYou: 'Scalability of AI spend',
    formula: 'SUM usd_cost / revenue',
    unit: 'percent',
    sample: 0.04,
    grain: 'org',
    sources: ['data_ai_agent_runs', 'billing'],
    calculate: 'Window usd_cost / recognized revenue. Keep this below the human-cost ratio.',
    wire: 'proxy',
  },
  {
    id: 'support_cost_as_pct_revenue',
    name: 'Support cost as % revenue',
    group: 'economics',
    tellsYou: 'Hidden burden',
    formula: 'PM+support human cost / revenue',
    unit: 'percent',
    sample: 0.18,
    grain: 'org',
    sources: ['data_ticket_user_assignees', 'billing'],
    calculate: 'Hours on non-build steps × rate / revenue.',
    wire: 'needs_event',
  },
  {
    id: 'cost_by_agent',
    name: 'Cost by agent',
    group: 'economics',
    tellsYou: 'Detective vs Builder vs Planner vs Mo',
    formula: 'SUM usd_cost GROUP BY agent_key',
    unit: 'usd',
    sample: 174,
    grain: 'agent_key',
    sources: ['data_ai_agent_runs.agent_key'],
    calculate: 'Direct group by. Chart uses SAMPLE_COST_BY_AGENT. Sample 174 is the window total.',
    wire: 'existing',
  },
  {
    id: 'cost_by_request_type',
    name: 'Cost by request type',
    group: 'economics',
    tellsYou: 'Questions may be cheap, changes expensive',
    formula: 'Average usd_cost for question vs change vs interrupt',
    unit: 'usd',
    sample: 9.4,
    grain: 'request type',
    sources: ['data_requests.is_interrupt', 'data_mo_tasks', 'data_ai_agent_runs'],
    calculate: 'Bucket: question (no ticket), change (ticket), interrupt (is_interrupt). Sample is the change-request average.',
    wire: 'existing',
  },
  {
    id: 'cost_of_failed_attempts',
    name: 'Cost of failed attempts',
    group: 'economics',
    tellsYou: 'AI waste',
    formula: 'SUM usd_cost where run did not contribute to the accepted outcome',
    unit: 'usd',
    sample: 31.6,
    grain: 'request',
    sources: ['data_ai_agent_runs'],
    calculate: 'Runs with status=error, aborted, or superseded retries before the successful run on the same ticket.',
    wire: 'existing',
  },
  {
    id: 'cost_of_founder_rescue',
    name: 'Cost of founder rescue',
    group: 'economics',
    tellsYou: 'Pilot reality',
    formula: 'Founder hours × rate + AI already spent on those outcomes',
    unit: 'usd',
    sample: 220,
    grain: 'rescued request',
    sources: ['founder_rescue_rate proxy', 'human cost'],
    calculate: 'Subset of human+AI cost where founder_rescue fired. Painful and useful.',
    wire: 'proxy',
  },
  {
    id: 'revenue_per_employee',
    name: 'Revenue per employee involved in delivery',
    group: 'economics',
    tellsYou: 'Service leverage',
    formula: 'Revenue / delivery FTE',
    unit: 'usd',
    sample: 18400,
    grain: 'FTE / month',
    sources: ['billing', 'data_user_roles'],
    calculate: 'Window revenue / (PM+engineer FTE). Sample is monthly-ish for a small pilot — labeled sample.',
    wire: 'needs_event',
  },
  {
    id: 'revenue_per_engineer',
    name: 'Revenue per engineer',
    group: 'economics',
    tellsYou: 'Classic productivity',
    formula: 'Revenue / engineering FTE',
    unit: 'usd',
    sample: 27600,
    grain: 'FTE',
    sources: ['billing', 'data_user_roles'],
    calculate: 'Same as above, engineers only.',
    wire: 'needs_event',
  },
  {
    id: 'revenue_per_client_support_fte',
    name: 'Revenue per active client-support FTE',
    group: 'economics',
    tellsYou: 'Agency scalability (the 10/20 clients per PM story)',
    formula: 'Revenue / PM-support FTE',
    unit: 'usd',
    sample: 55200,
    grain: 'FTE',
    sources: ['billing', 'concurrent_active_clients_per_pm'],
    calculate: 'Pair with concurrent_active_clients_per_pm. This is the Advante staffing proof.',
    wire: 'needs_event',
  },
]

export const GROUPS: Array<{ id: MetricGroup; label: string; blurb: string }> = [
  {
    id: 'client',
    label: 'Client experience',
    blurb: 'Better service: speed, wait, chat-as-front-door, and handoffs that already have evidence.',
  },
  {
    id: 'coordination',
    label: 'Coordination',
    blurb: 'The agency needs less PM: touches, auto-routing, clients per PM.',
  },
  {
    id: 'engineering',
    label: 'Engineering',
    blurb: 'More software per person: attention per accepted outcome, not lines of code.',
  },
  {
    id: 'autonomy',
    label: 'AI autonomy',
    blurb: 'Layered autonomy — never one generic percentage. Mandatory approval ≠ rescue.',
  },
  {
    id: 'reliability',
    label: 'Reliability',
    blurb: 'Runtime vs reasoning. Timeouts, budgets, evidence, false greens.',
  },
  {
    id: 'quality',
    label: 'Delivery quality',
    blurb: 'So we do not optimize speed while shipping worse software.',
  },
  {
    id: 'preview',
    label: 'Preview & review',
    blurb: 'Motionode’s client-visible funnel: generate → open → approve → release.',
  },
  {
    id: 'economics',
    label: 'Economics',
    blurb: 'Cost per accepted client outcome — not cost per DeepSeek call.',
  },
]

export function metricsByGroup(group: MetricGroup): MetricDef[] {
  return METRICS.filter((m) => m.group === group)
}

export function headlineMetrics(): MetricDef[] {
  return METRICS.filter((m) => m.headline)
}

export function metricById(id: string): MetricDef | undefined {
  return METRICS.find((m) => m.id === id)
}
